import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Button,
  Caption,
  Card,
  FormLayout,
  // PageActions,
  Select,
  Stack,
  TextField,
  TextStyle,
} from "@shopify/polaris";
import { useMutation, useQuery } from "@apollo/react-hooks";

// import helper
import { baseHelper, errorHelper } from "lib/helpers";

// import constant
import constant from "lib/constant/constant";

// import component
import { Sheet, SkeletonCard, Toast } from "lib/components";

// import mutations
import { ADD_PRODUCT_INVENTORY } from "app/product/apollo/mutations";

//  import queries
import { GET_PRODUCT_AND_SELLER_PRODUCT } from "app/product/apollo/queries";

// import data
import editData from "app/product/modules/operator/features/edit/config/editData";

import { GET_PRODUCT_FORM } from "app/setup/apollo/queries";

// import context
import { PrivateContext } from "lib/context/privateContext";
import { ProductContext } from "app/product/modules/generic/context";

const EditInventory = () => {
  const { handleTabChange, setBanner, setDescription, setTitle } = useContext(ProductContext);
  const { match, cms } = useContext(PrivateContext);
  const { gql, SHOPIFY = "", INVENTORY_MANAGEMENT, CRITICAL } = constant;
  const [message, setMessage] = useState("");
  const [sheetActive, setSheetActive] = useState(false);
  const [disabledButton, setDisabledButton] = useState(true);
  const [skuData, setSkuData] = useState("");
  const [quantityData, setQuantityData] = useState("");
  const [barcodeData, setBarcodeData] = useState("");
  const [inventoryData, setInventoryData] = useState("");
  // const [sheetContent, setSheetContent] = useState("");
  const [productFormSetting, setProductFormSetting] = useState();
  const [value, setValue] = useState({});
  setDescription(cms("label.editInventory"));
  setTitle(cms("label.title.inventory"));
  const handleChange = useCallback(
    (fieldName, fieldValue) => {
      const fieldData = [constant.SKU];
      setValue({
        ...value,
        [fieldName]: (!fieldData.includes(fieldName) && parseFloat(0)) || fieldValue,
      });
    },
    [value]
  );

  const { loading: productLoading, data: productData } = useQuery(GET_PRODUCT_AND_SELLER_PRODUCT, {
    variables: { input: { _id: match.params.id } },
  });

  const { loading: loadingProductForm, data: dataProductForm } = useQuery(GET_PRODUCT_FORM);

  useEffect(() => {
    if (dataProductForm) {
      const productLabel = baseHelper.getResponseData(dataProductForm, gql.GET_PRODUCT_FORM_SETTING);
      if (productLabel && productLabel.title) {
        setSkuData(productLabel.sku);
        setBarcodeData(productLabel.barcode);
        setInventoryData(productLabel.inventory);
        setQuantityData(productLabel.quantity);
        setProductFormSetting(productLabel);
      }
    }
  }, [dataProductForm, gql.GET_PRODUCT_FORM_SETTING]);

  const acceptOnlyValidInput = (val) => {
    return (baseHelper.validateWholeNumber(val) && val) || (val !== "" && value.quantity) || 0;
  };

  // const learnMore = (productItem, title) => {
  //   setSheetActive(true);
  //   setSheetTitle(title);
  //   setSheetContent(cms("label.todo")`${productItem}`);
  // };

  const primaryAction = {
    content: cms("label.done"),
    onAction: () => setSheetActive(false),
  };

  const secondaryAction = {
    content: cms("label.cancel"),
    onAction: () => setSheetActive(false),
  };

  useEffect(() => {
    if (productData) {
      const productResponse = baseHelper.getResponseData(productData, gql.GET_PRODUCT_AND_SELLER_PRODUCT) || {};
      setValue(productResponse.sellerProduct);
    }
  }, [gql.GET_PRODUCT_AND_SELLER_PRODUCT, productData]);

  const [addProductInventory, { loading: addInventoryLoading }] = useMutation(ADD_PRODUCT_INVENTORY);

  const productListError = baseHelper.getResponseError(productData, gql.GET_PRODUCT_AND_SELLER_PRODUCT);

  useEffect(() => {
    if (productListError) {
      setBanner({ isOpen: true, status: CRITICAL, title: productListError });
    }
  }, [CRITICAL, cms, productListError, setBanner]);

  if (productLoading || loadingProductForm) {
    return <SkeletonCard />;
  }

  const onFormSubmit = async () => {
    const formValues = {
      productId: value.productId,
      sku: value.sku,
      barcode: value.barcode,
      inventoryManagement: value.inventoryManagement,
    };
    formValues.quantity = parseInt(value.quantity, 10);

    const dataToCheck = {
      quantity: formValues.quantity,
      sku: value.sku,
      barcode: value.barcode,
      inventory: value.inventoryManagement,
    };

    const requiredKeys =
      (productFormSetting &&
        Object.keys(productFormSetting).filter(
          (item) => productFormSetting[item] && productFormSetting[item].isRequired
        )) ||
      [];

    let errorField = "";
    if (requiredKeys && requiredKeys.length) {
      requiredKeys.forEach((key) => {
        const valueData = dataToCheck[key];
        if (!valueData && constant.INVENTORY_KEY.includes(key)) {
          errorField = productFormSetting[key].label || cms(`label.${key}`) || key;
        }
      });
    }

    if (errorField) {
      const banner = {
        action: null,
        isOpen: true,
        status: CRITICAL,
        title: `${baseHelper.ucFirst(errorField)} is required.`,
      };
      setBanner(banner);
      return;
    }

    await addProductInventory({ variables: { input: formValues } })
      .then((res) => {
        const responseError = baseHelper.getResponseError(res.data, gql.ADD_PRODUCT_INVENTORY);
        const responseData = baseHelper.getResponseData(res.data, gql.ADD_PRODUCT_INVENTORY);
        if (responseError) {
          setBanner({ isOpen: true, status: CRITICAL, title: responseError });
        }
        if (responseData) {
          setMessage(cms("message.success.inventory"));
          setBanner({ isOpen: false, status: "", title: "" });
          setTimeout(() => {
            handleTabChange(2);
          }, 1500);
        }
      })
      .catch((exception) => {
        setBanner({ isOpen: true, status: CRITICAL, title: errorHelper.parse(exception) });
      });
  };

  const verifyValues = () => {
    const formValues = {
      productId: value.productId,
      sku: value.sku,
      barcode: value.barcode,
      inventoryManagement: value.inventoryManagement,
    };

    formValues.quantity = parseInt(value.quantity, 10);

    const dataToCheck = {
      quantity: formValues.quantity,
      sku: value.sku,
      barcode: value.barcode,
      inventory: value.inventoryManagement,
    };

    const requiredKeys =
      (productFormSetting &&
        Object.keys(productFormSetting).filter(
          (item) => productFormSetting[item] && productFormSetting[item].isRequired
        )) ||
      [];

    let errorField = "";
    if (requiredKeys && requiredKeys.length) {
      requiredKeys.forEach((key) => {
        const valueData = dataToCheck[key];
        if (!valueData && constant.INVENTORY_KEY.includes(key)) {
          errorField = productFormSetting[key].label || cms(`label.${key}`) || key;
        }
      });
    }

    if (errorField) {
      const banner = {
        action: null,
        isOpen: true,
        status: CRITICAL,
        title: `${baseHelper.ucFirst(errorField)} is required.`,
      };
      setBanner(banner);
    } else {
      setBanner({ isOpen: false, status: "", title: "" });
      handleTabChange(2);
    }
  };

  const required = (labelValue) => {
    if (labelValue && labelValue.isRequired) {
      return "*";
    }
    return "";
  };

  const label = (val, defaultCms) => {
    if (val && val.label) {
      return val.label;
    }
    return defaultCms;
  };

  return (
    <>
      <Card
        title={[
          cms("label.organisation"),
          value && value.title && (
            <TextStyle variation="subdued">
              {value.updatedAt && <Caption>{`${baseHelper.lastUpdateDate(value.updatedAt)}`}</Caption>}
            </TextStyle>
          ),
        ]}
        id="productOrganisation"
        // actions={[
        //   {
        //     content: "Learn more",
        //     onAction: () => {
        //       learnMore("productOrganisation", cms("label.organisation"));
        //     },
        //   },
        // ]}
      >
        <Card.Section>
          <p>{cms("label.caption.organization")}</p>
          <br />
          <FormLayout>
            <FormLayout.Group>
              <TextField
                id="sku"
                label={`${label(skuData, cms("label.sku"))} ${required(skuData)}`}
                value={(value && value.sku) || ""}
                onChange={(val) => {
                  handleChange(constant.SKU, val);
                  setDisabledButton(false);
                }}
                maxLength={16}
              />
              <TextField
                id="barcode"
                label={`${label(barcodeData, cms("label.barcode"))} ${required(barcodeData)}`}
                value={(value && value.barcode) || ""}
                onChange={(val) => {
                  handleChange(constant.BARCODE, val);
                  setDisabledButton(false);
                }}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                id="policySelection"
                label={`${label(inventoryData, cms("label.inventory"))} ${required(inventoryData)}`}
                options={editData(cms).policyOption}
                value={(value && value.inventoryManagement) || ""}
                onChange={(val) => {
                  handleChange(INVENTORY_MANAGEMENT, val);
                  setDisabledButton(false);
                }}
              />
              {value && value.inventoryManagement === constant.SHOPIFY ? (
                <TextField
                  id="inventoryQuantity"
                  disabled={value.inventoryManagement !== SHOPIFY}
                  label={`${label(quantityData, cms("label.quantity"))} ${required(quantityData)}`}
                  value={(value && value.quantity && value.quantity.toString()) || ""}
                  min={0}
                  maxLength={10}
                  onChange={(val) => {
                    handleChange(constant.QUANTITY, acceptOnlyValidInput(val));
                    setDisabledButton(false);
                  }}
                />
              ) : (
                ""
              )}
              <div className="toast">
                <Toast message={message} />
              </div>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
      <br />
      <Stack>
        <Stack.Item fill>
          <Button onClick={() => handleTabChange(0)}>{cms("button.previous")}</Button>
        </Stack.Item>
        {value && value.status && value.status.toLowerCase() === constant.NEW && (
          <Stack.Item>
            <Button
              onClick={() => {
                verifyValues();
              }}
            >
              {cms("button.skip")}
            </Button>
          </Stack.Item>
        )}
        <Stack.Item>
          <Button primary onClick={() => onFormSubmit()} disabled={disabledButton} loading={addInventoryLoading}>
            {cms("button.save")}
          </Button>
        </Stack.Item>
      </Stack>
      {/* <PageActions
        primaryAction={{
          content: cms("button.save"),
          onAction: () => onFormSubmit(),
          loading: addInventoryLoading,
          disabled: disabledButton,
        }}
        secondaryActions={[
          {
            content: cms("button.previous"),
            onAction: () => {
              handleTabChange(0);
            },
          },
        ]}
      /> */}
      <Sheet
        // title={sheetTitle}
        isOpen={sheetActive}
        onClose={() => setSheetActive(false)}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        {/* {sheetContent} */}
      </Sheet>
    </>
  );
};
export default EditInventory;
